import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success: false;
  message?: string;
  error?: string;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const json = (await res.json()) as ApiSuccessResponse<T> | ApiErrorResponse;
  if (!res.ok) {
    const msg =
      (json as ApiErrorResponse).message ||
      (json as ApiErrorResponse).error ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return (json as ApiSuccessResponse<T>).data;
}

// ---------------------------------------------------------------------------
// List leads
// ---------------------------------------------------------------------------

export interface RenewalListItem {
  id: string;
  leadNo: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  status: string;
  stage: string | null;
  markName: string | null;
  createdAt: string;
}

export interface RenewalListResult {
  items: RenewalListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type RenewalStageFilter = "DISCUSSION" | "FILING" | "STATUS";
export type RenewalSort = "newest" | "oldest";

export interface ListRenewalLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  stage?: RenewalStageFilter;
  sort?: RenewalSort;
}

export async function listRenewalLeads(
  params: ListRenewalLeadsParams = {}
): Promise<RenewalListResult> {
  const q = new URLSearchParams();
  if (params.page != null) q.set("page", String(params.page));
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.search) q.set("search", params.search);
  if (params.status) q.set("status", params.status);
  if (params.stage) q.set("stage", params.stage);
  if (params.sort) q.set("sort", params.sort);

  const res = await apiFetch(`${API_BASE}/renewal/leads?${q.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return parseResponse<RenewalListResult>(res);
}

// ---------------------------------------------------------------------------
// Next lead number
// ---------------------------------------------------------------------------

export async function getRenewalNextLeadNumber(): Promise<{ leadNo: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/discussion/next-lead-number`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadNo: string }>(res);
}

// ---------------------------------------------------------------------------
// Get discussion details (edit / prefill mode)
// ---------------------------------------------------------------------------

export interface RenewalApplicationDetailsView {
  addressee: string | null;
  formOfAddress: string | null;
  firmName: string | null;
  place: string | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
}

export interface RenewalApplicationDocumentView {
  documentId: string;
  fileKey: string;
  filename: string;
  downloadUrl: string | null;
}

export interface RenewalApplicationView {
  applicationId: string;
  tmNo: string;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  validityDate: string | null;
  userDate: string | null;
}

export interface RenewalDiscussionDetails {
  leadId: string;
  leadNo: string;
  module: string;
  stage: string | null;
  initiationType: string | null;
  referenceLeadId: string | null;
  referenceLead: { id: string; leadNo: string; module: string } | null;
  preliminary: {
    markName: string | null;
    contactName: string;
    phone: string | null;
    email: string | null;
    callTime: string | null;
  };
  applicationDetails: RenewalApplicationDetailsView | null;
  numberOfApplications: number;
  applications: RenewalApplicationView[];
  documents: RenewalApplicationDocumentView[];
}

export async function getRenewalDiscussionDetails(
  leadId: string
): Promise<RenewalDiscussionDetails> {
  const res = await apiFetch(`${API_BASE}/renewal/discussion/${leadId}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return parseResponse<RenewalDiscussionDetails>(res);
}

// ---------------------------------------------------------------------------
// Create preliminary discussion
// ---------------------------------------------------------------------------

export type RenewalInitiationType = "CLIENT_INITIATED" | "EXISTING_LEAD";

export interface CreateRenewalDiscussionPayload {
  markName: string;
  contactName: string;
  phone: string;
  email?: string;
  callTime: string;
  initiationType: RenewalInitiationType;
  referenceLeadId?: string;
}

export interface CreateRenewalDiscussionResponse {
  leadId: string;
  leadNo: string;
  initiationType: RenewalInitiationType;
  referenceLeadId: string | null;
}

export async function createRenewalDiscussion(
  payload: CreateRenewalDiscussionPayload
): Promise<CreateRenewalDiscussionResponse> {
  const body: Record<string, unknown> = { ...payload };
  if (payload.initiationType === "CLIENT_INITIATED") {
    delete body.referenceLeadId;
  }
  const res = await apiFetch(`${API_BASE}/renewal/discussion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseResponse<CreateRenewalDiscussionResponse>(res);
}

// ---------------------------------------------------------------------------
// Update preliminary discussion
// ---------------------------------------------------------------------------

export interface UpdateRenewalPreliminaryPayload {
  markName?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  callTime?: string;
}

export async function updateRenewalPreliminary(
  leadId: string,
  payload: UpdateRenewalPreliminaryPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/discussion/${leadId}/preliminary`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Step 2: Application Details from Client — form submission + documents
// ---------------------------------------------------------------------------

export interface RenewalApplicationInput {
  tmNo: string;
  dateOfFiling?: string | null;
  mark?: string | null;
  typeOfMark?: string | null;
  classNumber?: number | null;
  validityDate?: string | null;
  userDate?: string | null;
}

export interface SubmitRenewalApplicationDetailsPayload {
  addressee?: string;
  formOfAddress?: string;
  firmName?: string;
  place?: string;
  applicant?: string;
  applicantAddress?: string;
  applicantType?: string;
  signatoryName?: string;
  signatoryDesignation?: string;
  applications?: RenewalApplicationInput[];
}

export async function submitRenewalApplicationDetails(
  leadId: string,
  payload: SubmitRenewalApplicationDetailsPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/discussion/${leadId}/application-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<{ leadId: string }>(res);
}

export interface RenewalUploadPresignedFile {
  key: string;
  bucket: string;
  url: string;
}

interface RenewalPresignDocumentsResult {
  files: RenewalUploadPresignedFile[];
}

export async function generateRenewalUploadPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<RenewalUploadPresignedFile[]> {
  const res = await apiFetch(
    `${API_BASE}/renewal/discussion/${leadId}/documents/presign`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files }),
    }
  );
  const data = await parseResponse<RenewalPresignDocumentsResult>(res);
  return data.files;
}

export interface RenewalDocumentRegistrationInput {
  fileKey: string;
  filename: string;
}

export async function registerRenewalDocuments(
  leadId: string,
  documents: RenewalDocumentRegistrationInput[]
): Promise<{ leadId: string; registeredCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/discussion/${leadId}/documents`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ documents }),
    }
  );
  return parseResponse<{ leadId: string; registeredCount: number }>(res);
}

export async function deleteRenewalDocument(
  leadId: string,
  documentId: string
): Promise<{ leadId: string; documentId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/discussion/${leadId}/documents/${documentId}`,
    { method: "DELETE", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ leadId: string; documentId: string }>(res);
}

// ---------------------------------------------------------------------------
// Static option lists — mirror Assignment/Follow-up modules so the renewal
// discussion screens stay visually consistent. Renewal backend stores
// `addressee` as a single string; the dropdown surfaces the same designation
// choices and falls back to a free-text "Name" when the user picks NAME.
// ---------------------------------------------------------------------------

export const RENEWAL_ADDRESSEE_DESIGNATIONS = [
  "The Proprietor",
  "The Managing Partner",
  "The Designated Partner",
  "The Managing Director",
  "The Managing Trustee",
  "The Chairman",
  "NAME",
] as const;

export const RENEWAL_APPLICANT_TYPES = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  {
    value: "LIMITED_LIABILITY_PARTNERSHIP",
    label: "Limited Liability Partnership",
  },
  { value: "OTHERS", label: "Others" },
] as const;

export const RENEWAL_FORM_OF_ADDRESS_OPTIONS = ["Sir", "Madam"] as const;

export const RENEWAL_PLACE_OPTIONS = [
  "Thiruvananthapuram",
  "Kollam",
  "Pathanamthitta",
  "Alappuzha",
  "Kottayam",
  "Idukki",
  "Ernakulam",
  "Thrissur",
  "Palakkad",
  "Malappuram",
  "Kozhikode",
  "Wayanad",
  "Kannur",
  "Kasaragod",
] as const;

export const RENEWAL_TYPE_OF_MARK_OPTIONS = [
  "Word",
  "Logo",
  "Label",
  "Slogan",
] as const;

export const RENEWAL_CLASS_NUMBERS = Array.from(
  { length: 44 },
  (_, i) => i + 1
);

// ---------------------------------------------------------------------------
// Skip step (Progress button)
// ---------------------------------------------------------------------------

export interface SkipRenewalStepResponse {
  step: string;
  status: string;
}

export async function skipRenewalStep(
  leadId: string,
  step: string
): Promise<SkipRenewalStepResponse> {
  const res = await apiFetch(
    `${API_BASE}/renewal/discussion/${leadId}/workflow/skip-step`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ step }),
    }
  );
  return parseResponse<SkipRenewalStepResponse>(res);
}

// ---------------------------------------------------------------------------
// Workflow progress
// ---------------------------------------------------------------------------

export interface RenewalProgressStep {
  slug: string;
  label: string;
  stage: string;
  stepType: string;
  parentStep?: string;
  isCompleted: boolean;
  isSkipped: boolean;
}

export interface RenewalProgressResponse {
  currentStep: string | null;
  currentSection: string | null;
  maxStepReached: string | null;
  steps: RenewalProgressStep[];
}

export async function getRenewalProgress(
  leadId: string
): Promise<RenewalProgressResponse> {
  const res = await apiFetch(
    `${API_BASE}/renewal/discussion/${leadId}/progress`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalProgressResponse>(res);
}

/** Backend RenewalStep enum slugs used for workflow progress lookup. */
export const RenewalWorkflowStep = {
  RN_S1_01_PreliminaryDiscussion: "RN_S1_01_PRELIMINARY_DISCUSSION",
  RN_S1_02_ApplicationDetailsFromClient:
    "RN_S1_02_APPLICATION_DETAILS_FROM_CLIENT",
  RN_S1_03_InvoiceToClient: "RN_S1_03_INVOICE_TO_CLIENT",
  RN_S1_04_PaymentFromClient: "RN_S1_04_PAYMENT_FROM_CLIENT",
  RN_S2_01_StampPaperDetailsToVendor: "RN_S2_01_STAMP_PAPER_DETAILS_TO_VENDOR",
  RN_S2_02_StampPaperFromVendor: "RN_S2_02_STAMP_PAPER_FROM_VENDOR",
  RN_S2_03a_PoAFieldsSaved: "RN_S2_03a_PoA_FIELDS_SAVED",
  RN_S2_03b_PoAPdfGenerated: "RN_S2_03b_PoA_PDF_GENERATED",
  RN_S2_04_FileFormsForRenewal: "RN_S2_04_FILE_FORMS_FOR_RENEWAL",
  RN_S2_05_InformClientOfFiling: "RN_S2_05_INFORM_CLIENT_OF_FILING",
  RN_S3_01_StatusDocuments: "RN_S3_01_STATUS_DOCUMENTS",
  RN_S3_02_NoticeToClient: "RN_S3_02_NOTICE_TO_CLIENT",
  RN_S3_03_InformClientOfRenewal: "RN_S3_03_INFORM_CLIENT_OF_RENEWAL",
} as const;

// ---------------------------------------------------------------------------
// Step 3: Invoice to Client for Renewal
// ---------------------------------------------------------------------------

export interface RenewalInvoicePreviewApplication {
  id: string;
  tmNo: string;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  validityDate: string | null;
}

export interface RenewalInvoicePreview {
  addressee: string | null;
  firmName: string | null;
  place: string | null;
  formOfAddress: string | null;
  mark: string;
  selectedTypeOfMark: string;
  selectedClass: string;
  noOfApplications: number;
  profFeePerApp: number;
  govtFeePerApp: number;
  totalProfFee: number;
  totalGovtFee: number;
  totalCharges: number;
  discountProfFeeRenewal: number;
  totalDiscount: number;
  invoiceAmount: number;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  collectedAmount: number;
  outstandingAmount: number;
  applications: RenewalInvoicePreviewApplication[];
  invoiceDocumentUrl: string | null;
  invoiceDocumentFilename: string | null;
}

export async function getRenewalInvoicePreview(
  leadId: string
): Promise<RenewalInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/renewal/invoice/${leadId}/to-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalInvoicePreview>(res);
}

export interface SaveRenewalInvoicePayload {
  invoiceDate?: string;
  discountProfFeeRenewal?: number;
  includeInvoiceNo?: boolean;
  invoiceAmount?: number;
}

export async function saveRenewalInvoice(
  leadId: string,
  payload: SaveRenewalInvoicePayload
): Promise<RenewalInvoicePreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/invoice/${leadId}/to-client/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<RenewalInvoicePreview & { leadId: string }>(res);
}

export interface RenewalInvoicePdfResponse {
  s3Key: string;
  downloadUrl: string;
  filename: string;
}

export async function generateRenewalInvoicePdf(
  leadId: string
): Promise<RenewalInvoicePdfResponse> {
  const res = await apiFetch(
    `${API_BASE}/renewal/invoice/${leadId}/to-client/generate`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalInvoicePdfResponse>(res);
}

export interface SendRenewalInvoicePayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export interface SendRenewalInvoiceResponse {
  whatsAppLink?: string;
  emailSent?: boolean;
  leadId?: string;
}

export async function sendRenewalInvoiceToClient(
  leadId: string,
  payload: SendRenewalInvoicePayload = {}
): Promise<SendRenewalInvoiceResponse> {
  const res = await apiFetch(
    `${API_BASE}/renewal/invoice/${leadId}/to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<SendRenewalInvoiceResponse>(res);
}

export interface RenewalInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface RenewalInvoicePaymentMessagePreview {
  message: string;
  waLink: string;
  emailPreview: RenewalInvoiceEmailPreview;
}

export async function getRenewalInvoicePaymentMessagePreview(
  leadId: string
): Promise<RenewalInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/renewal/invoice/${leadId}/to-client/payment-message-preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalInvoicePaymentMessagePreview>(res);
}

export async function sendRenewalInvoicePaymentMessage(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/invoice/${leadId}/to-client/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ waLink: string }>(res);
}

// ---------------------------------------------------------------------------
// Step 4: Payment from Client for Renewal
// ---------------------------------------------------------------------------

export interface RecordRenewalPaymentPayload {
  /** Delta — amount received in this payment, added to any prior payments. */
  collectedAmount: number;
}

export interface RenewalPaymentRecordResponse {
  invoiceAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
}

export async function recordRenewalPaymentFromClient(
  leadId: string,
  payload: RecordRenewalPaymentPayload
): Promise<RenewalPaymentRecordResponse> {
  const res = await apiFetch(`${API_BASE}/renewal/invoice/${leadId}/payment`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseResponse<RenewalPaymentRecordResponse>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 1: Stamp Paper details to Vendor for Renewal
// ---------------------------------------------------------------------------

export interface RenewalStampPaperVendorOption {
  name: string;
  phone: string;
  isDefault?: boolean;
}

export interface RenewalStampPaperApplicationView {
  id: string;
  tmNo: string;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  validityDate: string | null;
}

export interface RenewalStampPaperPreview {
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  poaStamps: number;
  stampVendorName: string | null;
  stampVendorPhone: string | null;
  message: string | null;
  waLink: string | null;
  stampVendors: RenewalStampPaperVendorOption[];
  applications: RenewalStampPaperApplicationView[];
}

export async function getRenewalStampPaperPreview(
  leadId: string
): Promise<RenewalStampPaperPreview> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/stamp-paper/${leadId}/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalStampPaperPreview>(res);
}

export interface SaveRenewalStampPaperPayload {
  poaStamps?: number;
  stampVendorName?: string;
  stampVendorPhone?: string;
}

export async function saveRenewalStampPaper(
  leadId: string,
  payload: SaveRenewalStampPaperPayload
): Promise<RenewalStampPaperPreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/stamp-paper/${leadId}/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<RenewalStampPaperPreview & { leadId: string }>(res);
}

export async function sendRenewalStampPaperWhatsApp(
  leadId: string
): Promise<{ waLink: string; leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/stamp-paper/${leadId}/send`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<{ waLink: string; leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 2: Stamp Paper from Vendor for Renewal (vendor payment)
// ---------------------------------------------------------------------------

export type RenewalVendorPaymentStatus = "PAID" | "PENDING";

export interface RenewalStampPaperFromVendorPreview {
  /** 41g) PoA stamp count — read-only, entered at Step 1. */
  poaStamps: number;
  /** Auto-calculated: poaStamps × Rs. 110/-. */
  calculatedVendorAmount: number;
  /** User override (41i). Null means use the calculated amount. */
  vendorAmountPayable: number | null;
  /** Effective amount payable — override if set, otherwise calculated. */
  effectiveVendorAmount: number;
  /** 41j) Paid to Vendor. */
  vendorPaid: boolean;
  vendorPaymentStatus: RenewalVendorPaymentStatus;
}

export async function getRenewalStampPaperFromVendorPreview(
  leadId: string
): Promise<RenewalStampPaperFromVendorPreview> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/stamp-paper-from-vendor/${leadId}/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalStampPaperFromVendorPreview>(res);
}

export interface SaveRenewalStampPaperFromVendorPayload {
  /**
   * 41i) Amount Payable to Stamp Vendor. Send null to clear an override and
   * fall back to the auto-calculated amount.
   */
  vendorAmountPayable?: number | null;
  /** 41j) Paid to Vendor (true = paid, false = pending payment). */
  vendorPaid?: boolean;
}

export async function saveRenewalStampPaperFromVendor(
  leadId: string,
  payload: SaveRenewalStampPaperFromVendorPayload
): Promise<RenewalStampPaperFromVendorPreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/stamp-paper-from-vendor/${leadId}/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<RenewalStampPaperFromVendorPreview & { leadId: string }>(
    res
  );
}

// ---------------------------------------------------------------------------
// Stage 2, Step 3: Preparation of Power of Attorney for Renewal
// ---------------------------------------------------------------------------

export interface RenewalPoaPdfMeta {
  key: string;
  url: string;
  filename: string;
}

export interface RenewalPoaContext {
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  mark: string | null;
  /** Editable — ISO date string (YYYY-MM-DD). */
  dateOfPowerOfAttorney: string | null;
  /** Read-only formatted date (e.g., "12th day of January 2025"). */
  dateOfPowerOfAttorneyFormatted: string | null;
  /** Editable. */
  descriptionOfApplicant: string | null;
  /** Derived from applicantType — used for backend PDF templating. */
  templateVariant: string | null;
  pdf: RenewalPoaPdfMeta | null;
  pdfIsStale: boolean;
  pdfGeneratedAt: string | null;
  pdfGeneratedBy: string | null;
}

export interface SaveRenewalPoaPayload {
  /** ISO date YYYY-MM-DD. Optional — only updates if provided. */
  dateOfPowerOfAttorney?: string;
  descriptionOfApplicant?: string;
}

export async function getRenewalPoaContext(
  leadId: string
): Promise<RenewalPoaContext> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/poa/${leadId}/context`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalPoaContext>(res);
}

export async function saveRenewalPoa(
  leadId: string,
  payload: SaveRenewalPoaPayload
): Promise<RenewalPoaContext> {
  const res = await apiFetch(`${API_BASE}/renewal/filing/poa/${leadId}/save`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseResponse<RenewalPoaContext>(res);
}

export async function generateRenewalPoaPdf(
  leadId: string,
  force: boolean = false
): Promise<RenewalPoaPdfMeta> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/poa/${leadId}/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ force }),
    }
  );
  return parseResponse<RenewalPoaPdfMeta>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 4: File forms for Renewal
// ---------------------------------------------------------------------------

export interface RenewalFileFormsApplicationItem {
  applicationId: string;
  tmNo: string;
  /** Date of Filing of the original application (read-only). */
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  validityDate: string | null;
  /** 41k) Date of Filing Renewal form (YYYY-MM-DD). */
  dateOfFilingRenewal: string | null;
  renewalFormPdfFileKey: string | null;
  renewalFormPdfFilename: string | null;
  /** Presigned download URL — null when no PDF has been uploaded yet. */
  pdfDownloadUrl: string | null;
  isPdfUploaded: boolean;
}

export interface RenewalFileFormsResponse {
  applications: RenewalFileFormsApplicationItem[];
}

export interface SaveRenewalFileFormsItem {
  applicationId: string;
  /** YYYY-MM-DD; null clears; omit to leave unchanged. */
  dateOfFilingRenewal?: string | null;
  renewalFormPdfFileKey?: string | null;
  renewalFormPdfFilename?: string | null;
}

export interface SaveRenewalFileFormsPayload {
  applications: SaveRenewalFileFormsItem[];
}

export async function getRenewalFileForms(
  leadId: string
): Promise<RenewalFileFormsResponse> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/${leadId}/file-forms`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalFileFormsResponse>(res);
}

export async function saveRenewalFileForms(
  leadId: string,
  payload: SaveRenewalFileFormsPayload
): Promise<RenewalFileFormsResponse & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/${leadId}/file-forms`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<RenewalFileFormsResponse & { leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 2, Step 5: Inform Client about Filing of Renewal
// ---------------------------------------------------------------------------

export interface RenewalInformClientOfFilingPreview {
  message: string;
  recipientPhone: string;
  applicationNumbers: string;
}

export interface RenewalInformClientOfFilingSendResult {
  leadId: string;
  waLink: string;
  message: string;
  sentViaApi: boolean;
}

export async function getRenewalInformClientOfFilingPreview(
  leadId: string
): Promise<RenewalInformClientOfFilingPreview> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/${leadId}/inform-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalInformClientOfFilingPreview>(res);
}

export async function sendRenewalInformClientOfFilingWhatsApp(
  leadId: string
): Promise<RenewalInformClientOfFilingSendResult> {
  const res = await apiFetch(
    `${API_BASE}/renewal/filing/${leadId}/inform-client/whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalInformClientOfFilingSendResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 1: Status Documents from Trademarks Registry
// ---------------------------------------------------------------------------

export interface RenewalStatusApplicationItem {
  applicationId: string;
  tmNo: string;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  /** 5c) Validity date of Registration (read-only — set at Stage 1, Step 2). */
  validityDate: string | null;
  /** 41l) Validity date of 1st Renewal — defaults to validityDate + 10 years, can be overridden. */
  validityDate1stRenewal: string | null;
  renewalIntimationPdfFileKey: string | null;
  renewalIntimationPdfFilename: string | null;
  renewalIntimationDownloadUrl: string | null;
  isRenewalIntimationUploaded: boolean;
  statusOfApplicationPdfFileKey: string | null;
  statusOfApplicationPdfFilename: string | null;
  statusOfApplicationDownloadUrl: string | null;
  isStatusOfApplicationUploaded: boolean;
}

export interface RenewalStatusPreview {
  applications: RenewalStatusApplicationItem[];
}

export interface SaveRenewalStatusItem {
  applicationId: string;
  /** YYYY-MM-DD; null clears the override (recalculates from validityDate); omit to leave unchanged. */
  validityDate1stRenewal?: string | null;
  renewalIntimationPdfFileKey?: string | null;
  renewalIntimationPdfFilename?: string | null;
  statusOfApplicationPdfFileKey?: string | null;
  statusOfApplicationPdfFilename?: string | null;
}

export interface SaveRenewalStatusPayload {
  applications: SaveRenewalStatusItem[];
}

export async function getRenewalStatusDocuments(
  leadId: string
): Promise<RenewalStatusPreview> {
  const res = await apiFetch(
    `${API_BASE}/renewal/status/${leadId}/status-documents`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalStatusPreview>(res);
}

export async function saveRenewalStatusDocuments(
  leadId: string,
  payload: SaveRenewalStatusPayload
): Promise<RenewalStatusPreview & { leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/renewal/status/${leadId}/status-documents`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<RenewalStatusPreview & { leadId: string }>(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 2: Notice to Client of Renewal (forward PDFs via WhatsApp + Email)
// ---------------------------------------------------------------------------

export type RenewalNoticePdfKind =
  | "renewal_intimation"
  | "status_of_application";

export interface RenewalNoticePdfItem {
  applicationId: string;
  tmNo: string;
  kind: RenewalNoticePdfKind;
  filename: string;
  previewMessage: string;
}

export interface RenewalNoticeToClientPreview {
  recipientPhone: string | null;
  recipientEmail: string | null;
  totalPdfsToForward: number;
  items: RenewalNoticePdfItem[];
}

export interface RenewalNoticeToClientSendResult {
  leadId: string;
  pdfsForwarded: number;
  waLinks: string[];
  sentViaApi: boolean;
  emailsSent: number;
}

export interface SendRenewalNoticeToClientPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
  applicationIds?: string[];
}

export async function getRenewalNoticeToClientPreview(
  leadId: string,
  applicationIds?: string[]
): Promise<RenewalNoticeToClientPreview> {
  const q = new URLSearchParams();
  applicationIds?.forEach((id) => q.append("applicationIds", id));
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const res = await apiFetch(
    `${API_BASE}/renewal/status/${leadId}/notice-to-client/preview${suffix}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalNoticeToClientPreview>(res);
}

export async function sendRenewalNoticeToClient(
  leadId: string,
  payload: SendRenewalNoticeToClientPayload = {}
): Promise<RenewalNoticeToClientSendResult> {
  const res = await apiFetch(
    `${API_BASE}/renewal/status/${leadId}/notice-to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<RenewalNoticeToClientSendResult>(res);
}

// ---------------------------------------------------------------------------
// Stage 3, Step 3: Inform Client about Renewal (completion)
// ---------------------------------------------------------------------------

export interface RenewalInformClientPreviewDocument {
  applicationId: string;
  tmNo: string;
  hasRenewalIntimation: boolean;
  renewalIntimationFilename: string | null;
  hasStatusOfApplication: boolean;
  statusOfApplicationFilename: string | null;
}

export interface RenewalInformClientAboutRenewalPreview {
  message: string;
  recipientPhone: string | null;
  recipientEmail: string | null;
  applicationNumbers: string;
  documents: RenewalInformClientPreviewDocument[];
  totalPdfsToEmail: number;
}

export interface RenewalInformClientAboutRenewalSendResult {
  leadId: string;
  waLink: string | null;
  sentViaApi: boolean;
  emailsSent: number;
  message: string;
}

export interface SendRenewalInformClientAboutRenewalPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export async function getRenewalInformClientAboutRenewalPreview(
  leadId: string
): Promise<RenewalInformClientAboutRenewalPreview> {
  const res = await apiFetch(
    `${API_BASE}/renewal/status/${leadId}/inform-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  return parseResponse<RenewalInformClientAboutRenewalPreview>(res);
}

export async function sendRenewalInformClientAboutRenewal(
  leadId: string,
  payload: SendRenewalInformClientAboutRenewalPayload = {}
): Promise<RenewalInformClientAboutRenewalSendResult> {
  const res = await apiFetch(
    `${API_BASE}/renewal/status/${leadId}/inform-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return parseResponse<RenewalInformClientAboutRenewalSendResult>(res);
}
